import {
  ROLLING_FIRST_RUN_DAYS,
  ROLLING_OVERLAP_DAYS,
  ROLLING_TIME_RANGE,
  TIME_RANGE_DAYS,
} from "@repo/adapter-core/date-window";
import { GLOBAL_TIME_RANGE_OPTIONS } from "@repo/adapter-core/types";
import { Badge } from "@repo/ui/badge";
import { Button } from "@repo/ui/button";
import { Input } from "@repo/ui/input";
import { Label } from "@repo/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@repo/ui/select";
import { AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";

export interface AdapterRuntimeConfig {
  listingType?: "buy" | "rent" | "sold" | "any";
  propertyTypes?: string[];
  dateFilterMode?: "relative" | "absolute";
  timeRange?: string;
  rollingFirstRunDays?: number;
  dateFrom?: string;
  dateTo?: string;
  minPrice?: number;
  maxPrice?: number;
  providerMode?: "auto" | "fallback" | "forced";
  provider?: "firecrawl_scrape" | "firecrawl_agent" | "zyte" | "direct" | "any";
  providerPreference?: string[];
}

const PROVIDERS = [
  { value: "firecrawl_scrape", label: "Firecrawl (Scrape)" },
  { value: "firecrawl_agent", label: "Firecrawl (Agent)" },
  { value: "zyte", label: "Zyte" },
  { value: "direct", label: "Direct" },
] as const;

const PROPERTY_TYPE_OPTIONS = [
  { value: "house", label: "House" },
  { value: "apartment-unit-flat", label: "Apartment / Unit / Flat" },
  { value: "townhouse", label: "Townhouse" },
  { value: "villa", label: "Villa" },
  { value: "land", label: "Land" },
  { value: "acreage", label: "Acreage" },
  { value: "rural", label: "Rural" },
] as const;

interface AdapterRuntimeConfigPanelProps {
  adapterName: string;
  config: AdapterRuntimeConfig;
  onChange: (config: AdapterRuntimeConfig) => void;
  isGlobal?: boolean;
  showAdvanced?: boolean;
  onToggleAdvanced?: () => void;
  warningMessage?: string;
}

function toDateInputValue(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function getDateMode(config: AdapterRuntimeConfig): "relative" | "absolute" {
  if (config.dateFilterMode) return config.dateFilterMode;
  if (config.dateFrom || config.dateTo) return "absolute";
  return "relative";
}

function getRelativeWindow(timeRange?: string): { from: string; to: string } | null {
  if (!timeRange || timeRange === "all") return null;
  const days = TIME_RANGE_DAYS[timeRange];
  if (!days) return null;

  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - days);

  return {
    from: toDateInputValue(start),
    to: toDateInputValue(end),
  };
}

export function AdapterRuntimeConfigPanel({
  adapterName,
  config,
  onChange,
  isGlobal = false,
  showAdvanced = true,
  onToggleAdvanced,
  warningMessage,
}: AdapterRuntimeConfigPanelProps) {
  const adapterId = adapterName.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const dateFilterMode = getDateMode(config);

  const normalize = (next: AdapterRuntimeConfig): AdapterRuntimeConfig => {
    const mode = getDateMode(next);
    if (mode === "relative") {
      return {
        ...next,
        dateFilterMode: "relative",
        dateFrom: undefined,
        dateTo: undefined,
      };
    }

    return {
      ...next,
      dateFilterMode: "absolute",
      timeRange: undefined,
    };
  };

  const update = (partial: Partial<AdapterRuntimeConfig>) => {
    onChange(normalize({ ...config, ...partial }));
  };

  const providerPreference = config.providerPreference || ["firecrawl_scrape", "zyte", "direct"];
  const selectedPropertyType = config.propertyTypes?.length === 1 ? config.propertyTypes[0] : "any";
  const toggleProviderPreference = (provider: string) => {
    const next = providerPreference.includes(provider)
      ? providerPreference.filter((item) => item !== provider)
      : [...providerPreference, provider];
    update({ providerPreference: next.length > 0 ? next : ["firecrawl_scrape"] });
  };
  const relativeWindow = getRelativeWindow(config.timeRange);

  return (
    <div className="space-y-4 p-3 border rounded-lg bg-muted/20">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {isGlobal ? "Global Crawl Defaults" : `${adapterName} Runtime & Broad Filters`}
      </p>

      {warningMessage && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-700 dark:text-amber-300">
          <p className="flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5" />
            {warningMessage}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor={`runtime-listing-type-${adapterId}`} className="text-xs">
            Listing Type
          </Label>
          <Select
            name={`runtime-listing-type-${adapterId}`}
            value={config.listingType || "any"}
            onValueChange={(value) =>
              update({ listingType: value as AdapterRuntimeConfig["listingType"] })
            }
          >
            <SelectTrigger id={`runtime-listing-type-${adapterId}`} className="h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any</SelectItem>
              <SelectItem value="sold">Sold</SelectItem>
              <SelectItem value="buy">For Sale</SelectItem>
              <SelectItem value="rent">For Rent</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={`runtime-property-type-${adapterId}`} className="text-xs">
            Property Type
          </Label>
          <Select
            name={`runtime-property-type-${adapterId}`}
            value={selectedPropertyType}
            onValueChange={(value) =>
              update({
                propertyTypes: value === "any" ? undefined : [value],
              })
            }
          >
            <SelectTrigger id={`runtime-property-type-${adapterId}`} className="h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any</SelectItem>
              {PROPERTY_TYPE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {isGlobal && (
          <div className="space-y-1.5">
            <Label htmlFor={`runtime-date-mode-${adapterId}`} className="text-xs">
              Date Filter Mode
            </Label>
            <Select
              name={`runtime-date-mode-${adapterId}`}
              value={dateFilterMode}
              onValueChange={(value) =>
                update({ dateFilterMode: value as "relative" | "absolute" })
              }
            >
              <SelectTrigger id={`runtime-date-mode-${adapterId}`} className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="relative">Relative window</SelectItem>
                <SelectItem value="absolute">Absolute dates</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {!isGlobal ? (
        <p className="text-[10px] text-muted-foreground">
          Date window: set once for the whole job (Global Defaults).
        </p>
      ) : dateFilterMode === "relative" ? (
        <div className="space-y-1.5">
          <Label htmlFor={`runtime-time-range-${adapterId}`} className="text-xs">
            Time Range
          </Label>
          <Select
            name={`runtime-time-range-${adapterId}`}
            value={config.timeRange || "all"}
            onValueChange={(value) => update({ timeRange: value })}
          >
            <SelectTrigger id={`runtime-time-range-${adapterId}`} className="h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GLOBAL_TIME_RANGE_OPTIONS.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {relativeWindow && (
            <p className="text-[10px] text-muted-foreground">
              Computed window for each run: {relativeWindow.from} {"->"} {relativeWindow.to}
            </p>
          )}
          {config.timeRange === ROLLING_TIME_RANGE && (
            <p className="text-[10px] text-muted-foreground">
              Each run covers sales since the job's last completed run, minus {ROLLING_OVERLAP_DAYS}{" "}
              days' overlap. With no completed run yet it covers the last{" "}
              {config.rollingFirstRunDays ?? ROLLING_FIRST_RUN_DAYS} days.
            </p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor={`runtime-date-from-${adapterId}`} className="text-xs">
              Date From
            </Label>
            <Input
              id={`runtime-date-from-${adapterId}`}
              type="date"
              className="h-8 text-sm"
              name={`runtime-date-from-${adapterId}`}
              value={config.dateFrom || ""}
              onChange={(event) => update({ dateFrom: event.target.value || undefined })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`runtime-date-to-${adapterId}`} className="text-xs">
              Date To
            </Label>
            <Input
              id={`runtime-date-to-${adapterId}`}
              type="date"
              className="h-8 text-sm"
              name={`runtime-date-to-${adapterId}`}
              value={config.dateTo || ""}
              onChange={(event) => update({ dateTo: event.target.value || undefined })}
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor={`runtime-min-price-${adapterId}`} className="text-xs">
            Min Price
          </Label>
          <Input
            id={`runtime-min-price-${adapterId}`}
            type="number"
            className="h-8 text-sm"
            name={`runtime-min-price-${adapterId}`}
            placeholder="Any"
            value={config.minPrice || ""}
            onChange={(event) =>
              update({
                minPrice: event.target.value ? parseInt(event.target.value, 10) : undefined,
              })
            }
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`runtime-max-price-${adapterId}`} className="text-xs">
            Max Price
          </Label>
          <Input
            id={`runtime-max-price-${adapterId}`}
            type="number"
            className="h-8 text-sm"
            name={`runtime-max-price-${adapterId}`}
            placeholder="Any"
            value={config.maxPrice || ""}
            onChange={(event) =>
              update({
                maxPrice: event.target.value ? parseInt(event.target.value, 10) : undefined,
              })
            }
          />
        </div>
      </div>

      {!isGlobal && onToggleAdvanced && (
        <Button
          type="button"
          variant="ghost"
          className="h-7 px-2 text-xs"
          onClick={onToggleAdvanced}
        >
          {showAdvanced ? (
            <>
              <ChevronUp className="h-3.5 w-3.5 mr-1" />
              Hide additional adapter settings
            </>
          ) : (
            <>
              <ChevronDown className="h-3.5 w-3.5 mr-1" />
              Show additional adapter settings
            </>
          )}
        </Button>
      )}

      {showAdvanced && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor={`runtime-provider-mode-${adapterId}`} className="text-xs">
                Provider Mode
              </Label>
              <Select
                name={`runtime-provider-mode-${adapterId}`}
                value={config.providerMode || "auto"}
                onValueChange={(value) =>
                  update({ providerMode: value as AdapterRuntimeConfig["providerMode"] })
                }
              >
                <SelectTrigger id={`runtime-provider-mode-${adapterId}`} className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (learned)</SelectItem>
                  <SelectItem value="fallback">Fallback chain</SelectItem>
                  <SelectItem value="forced">Forced provider</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`runtime-provider-${adapterId}`} className="text-xs">
                Forced Provider
              </Label>
              <Select
                name={`runtime-provider-${adapterId}`}
                value={config.provider || "any"}
                onValueChange={(value) =>
                  update({ provider: value as AdapterRuntimeConfig["provider"] })
                }
              >
                <SelectTrigger id={`runtime-provider-${adapterId}`} className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  {PROVIDERS.map((provider) => (
                    <SelectItem key={provider.value} value={provider.value}>
                      {provider.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Provider Preference Order</Label>
            <div className="flex flex-wrap gap-1.5">
              {PROVIDERS.map((provider) => (
                <Badge
                  key={provider.value}
                  variant={providerPreference.includes(provider.value) ? "default" : "outline"}
                  className="cursor-pointer text-xs"
                  onClick={() => toggleProviderPreference(provider.value)}
                >
                  {provider.label}
                </Badge>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground">
              Click to include/exclude providers. Auto mode uses this as tie-break preference.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
